import { Component, Input, OnInit } from '@angular/core';
import { EntitlementsHelperService, LOGIN_INFO_REDUCER_NAME, UserInfoStateStructure, USER_ROLE } from '@ptl/account-management';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { BooleanInput, JsonInput } from '@ptl/global-components';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import {HTTPWrapperContext, REQUEST_METHODS, RequestConfigImplementation, XHR_STRATEGIES, AxwayTokenFetcherService} from '@ptl/xhr';
import {HttpHeaders} from '@angular/common/http';
import { BrandingConfigurationService } from '@ptl/brand';

//comment for release use
interface downloadLinks{
    linkLabel: string;
    linkUrl: string;
    linkValue: string;
}

interface ratingAreaMaps{
    linkLabel: string;
    linkUrl: string;
}

enum DOC_FORMATS{
  PDF = "pdf",
  EXCEL = "excel"
}

@Component({
    selector: 'app-rate-guides-download',
    templateUrl: './rate-guides-download.component.html',
    styleUrls: ['./rate-guides-download.component.scss'],
    standalone: false
})
export class RateGuidesDownloadComponent implements OnInit {

    @Input() @JsonInput() medRatingArea: ratingAreaMaps[];
    @Input() @JsonInput() medDownloadLinks: downloadLinks[];
    @Input() @JsonInput() dentDownloadLinks: downloadLinks[];
    @Input() @JsonInput() visDownloadLinks: downloadLinks[];
    @Input() @BooleanInput() forceHideMedical: string = 'false';
    @Input() @BooleanInput() forceHideDental: string = 'false';
    @Input() @BooleanInput() forceHideVision: string = 'false';
    @Input() medTitle: string;
    @Input() medSubtextOne: string;
    @Input() medSubtextTwo: string;
    @Input() medSubtextThree: string;
    @Input() medPdfDownloadBtn: string;
    @Input() medExlDownloadBtn: string;
    @Input() medFormErrorMsg: string;
    @Input() dentTitle: string;
    @Input() dentSubtextOne: string;
    @Input() dentSubtextTwo: string;
    @Input() dentSubtextThree: string;
    @Input() dentPdfDownloadBtn: string;
    @Input() dentExlDownloadBtn: string;
    @Input() dentFormErrorMsg: string;
    @Input() visTitle: string;
    @Input() visSubtextOne: string;
    @Input() visSubtextTwo: string;
    @Input() visSubtextThree: string;
    @Input() visPdfDownloadBtn: string;
    @Input() visExlDownloadBtn: string;
    @Input() visFormErrorMsg: string;

    loginData: UserInfoStateStructure;
    lob = 'individual'

    medicalDownloadForm: FormGroup;
    dentalDownloadForm: FormGroup;

    visionDownloadForm: FormGroup;
    isUserInternal: boolean;

    currentYear: string; 

    constructor(
        private entitlementService: EntitlementsHelperService,
        private reducerUtilityService: ReducerUtilityService,
        private fb: UntypedFormBuilder,
        private httpClient: HTTPWrapperContext,
        private brandService: BrandingConfigurationService,
    ) {
      this.medicalDownloadForm = this.fb.group({
        medicalDownloadSelection: this.fb.control(null, [
          Validators.required
        ]),
      });
      this.dentalDownloadForm = this.fb.group({
        dentalDownloadSelection: this.fb.control(null, [
          Validators.required
        ]),
      });
      this.visionDownloadForm = this.fb.group({
        visionDownloadSelection: this.fb.control(null, [
          Validators.required
        ]),
      });
     }

    async ngOnInit() {

        this.currentYear = String(new Date().getFullYear());

        // ENTITLEMENTS.
        this.lob = await this.entitlementService.getCurrentLobPromise();
        this.loginData = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);

        this.isUserInternal = this.loginData?.userInfo?.role.toLowerCase() === USER_ROLE.INTERNAL;

    }

    async handleMedicalDownload(format: DOC_FORMATS) {
      if (!this.submitForm(this.medicalDownloadForm)) {
        return;
      }

      const selectedUrl = this.medicalDownloadForm.controls['medicalDownloadSelection'].value;
      if (!selectedUrl) {
        return;
      }

      const apiUrl = selectedUrl.replace('{{format}}', format);
      const response = await this.getSbcForCmcrstService(apiUrl);
      
      if (response !== null && response !== undefined) {
        this.saveByteArray(response, format);
      }
    }

    get medicalFormControls(): string {
      return this.medicalDownloadForm.controls as unknown as string;
    }
  
    get medicalFormValuesAsJson(): string {
      return this.medicalDownloadForm.value as string;
    }

    get dentalFormControls(): string {
      return this.dentalDownloadForm.controls as unknown as string;
    }
  
    get dentalFormValuesAsJson(): string {
      return this.dentalDownloadForm.value as string;
    }

    get visionFormControls(): string {
      return this.visionDownloadForm.controls as unknown as string;
    }
  
    get visionFormValuesAsJson(): string {
      return this.visionDownloadForm.value as string;
    }

    submitForm(form: FormGroup){
      Object.keys(form.controls).forEach(key => {
        form.controls[key].markAsDirty();
        form.controls[key].markAsTouched();
      })

      if(form.invalid){
        return false;
      }

      return true;
    }

    async getSbcForCmcrstService(url: string) {
      const isExcel = url.includes('/excel');
      const headers: { [key: string]: string } = {
        
      };

      if (url.includes('cmcrst')) {
        const host = window.location.hostname;
        let env = '';
        if (host.indexOf('env') >= 0) {
            env = 't' + host.substring(host.indexOf('env'), host.indexOf('.'));
        }
        headers['x-hmhs-test-backend'] = `wasinter${env}.highmark.com`;
      }

      const requestConfig = new RequestConfigImplementation({
          method: REQUEST_METHODS.GET,
          strategyConfigs: { forceModernUrl: true },
          headers: new HttpHeaders(headers),
          url: url,
          responseType: 'blob',
          accept: isExcel ? 'application/vnd.ms-excel' : 'application/pdf',
          contentType: isExcel ? 'application/vnd.ms-excel' : 'application/pdf'
      });
      
      this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS_V4);
      try {
          const response = await this.httpClient.makeRequest(requestConfig) as Blob;
          return response;
      } catch (e) {
          console.error('Failed to fetch SBC blob', e);
          return null;
      }
    }

    saveByteArray(byte: any, docType: string) {
      const isExcel = docType === 'excel';
      const blob = new Blob([byte], {
          type: isExcel ? 'application/vnd.ms-excel' : 'application/pdf'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = (isExcel ? 'rateguide.xls' : 'rateguide.pdf');

      // Firefox needs the link in the DOM
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Free memory after the download starts
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    }
}
